import React, { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Divider,
  Link,
  Chip,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  ArrowForward,
  OpenInNew,
  CheckCircleOutline,
  WarningAmber,
  ArrowBack,
  AccessTime,
  CalendarToday,
  Person,
  LinkedIn,
} from '@mui/icons-material';
import SEOHead from '../../components/SEO/SEOHead';
import BreadcrumbNav from '../../components/SEO/BreadcrumbNav';
import AtomicAnswer from '../../components/SEO/AtomicAnswer';
import FAQSection from '../../components/SEO/FAQSection';
import NewsletterSection from '../../components/Layout/NewsletterSection';
import TrustStrip from '../../components/Layout/TrustStrip';
import ArticleTable from '../../components/Blog/ArticleTable';
import RelatedArticles from '../../components/Blog/RelatedArticles';
import nataliaPhoto from '../../assets/img/author-natalia.jpg';
import { useTranslation } from 'react-i18next';

const HERO_IMAGE = '/images/blog/tarjetas-credito-newcomers-canada.jpg';
const AUTHOR = {
  name: 'Natalia Duarte',
  linkedin: 'https://www.linkedin.com/in/natalia-duarteh/',
  photo: nataliaPhoto,
};

const TarjetasNewcomersPage = () => {
  const { t, i18n } = useTranslation();
  const { lng } = useParams();
  const [takeawaysOpen, setTakeawaysOpen] = useState(false);

  const p = 'blog.tarjetasNewcomers';

  const TAGS = [t(`${p}.tag1`), t(`${p}.tag2`)];
  const DATE_TEXT = t(`${p}.date`);
  const READ_TIME = t(`${p}.readTime`);

  const NEWCOMER_PROGRAMS = [
    {
      banco: 'Scotiabank',
      programa: 'StartRight',
      elegibilidad: t(`${p}.tableRow1Eligibility`),
      ingresoVerificable: t(`${p}.tableYes`),
      limiteTipico: '$500 - $2,000',
    },
    {
      banco: 'RBC',
      programa: 'Newcomer Program',
      elegibilidad: t(`${p}.tableRow2Eligibility`),
      ingresoVerificable: t(`${p}.tableYes`),
      limiteTipico: '$500 - $1,500',
    },
    {
      banco: 'TD',
      programa: 'New to Canada',
      elegibilidad: t(`${p}.tableRow3Eligibility`),
      ingresoVerificable: t(`${p}.tableYes`),
      limiteTipico: '$500 - $1,500',
    },
    {
      banco: 'CIBC',
      programa: 'Newcomer Banking',
      elegibilidad: t(`${p}.tableRow4Eligibility`),
      ingresoVerificable: t(`${p}.tableYes`),
      limiteTipico: '$500 - $2,000',
    },
    {
      banco: 'BMO',
      programa: 'NewStart',
      elegibilidad: t(`${p}.tableRow5Eligibility`),
      ingresoVerificable: t(`${p}.tableYes`),
      limiteTipico: '$500 - $1,500',
    },
  ];

  const FAQS = [
    { question: t(`${p}.faq1Q`), answer: t(`${p}.faq1A`) },
    { question: t(`${p}.faq2Q`), answer: t(`${p}.faq2A`) },
    { question: t(`${p}.faq3Q`), answer: t(`${p}.faq3A`) },
    { question: t(`${p}.faq4Q`), answer: t(`${p}.faq4A`) },
    { question: t(`${p}.faq5Q`), answer: t(`${p}.faq5A`) },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: t(`${p}.schemaHeadline`),
    description: t(`${p}.schemaDescription`),
    datePublished: '2026-02-24',
    dateModified: '2026-02-24',
    author: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com' },
    publisher: {
      '@type': 'Organization',
      name: 'FinMaple',
      url: 'https://www.finmaple.com',
      logo: { '@type': 'ImageObject', url: 'https://www.finmaple.com/og-image.jpg' },
    },
    mainEntityOfPage: 'https://www.finmaple.com/blog/tarjetas-newcomers-que-piden-bancos',
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.article-headline', '.atomic-answer'],
    },
    inLanguage: i18n.language,
  };

  const CHECKLIST = [
    { title: t(`${p}.doc1Title`), detail: t(`${p}.doc1Detail`) },
    { title: t(`${p}.doc2Title`), detail: t(`${p}.doc2Detail`) },
    { title: t(`${p}.doc3Title`), detail: t(`${p}.doc3Detail`) },
    { title: t(`${p}.doc4Title`), detail: t(`${p}.doc4Detail`) },
    { title: t(`${p}.doc5Title`), detail: t(`${p}.doc5Detail`) },
  ];

  const SECURED_CARDS = [
    {
      name: 'Home Trust Secured Visa',
      desc: t(`${p}.secured1Desc`),
      tag: t(`${p}.secured1Tag`),
      tagColor: '#1E3A5F',
    },
    {
      name: 'Capital One Guaranteed Secured',
      desc: t(`${p}.secured2Desc`),
      tag: t(`${p}.secured2Tag`),
      tagColor: '#2E7D32',
    },
    {
      name: 'Neo Secured Credit',
      desc: t(`${p}.secured3Desc`),
      tag: t(`${p}.secured3Tag`),
      tagColor: '#7D5A00',
    },
  ];

  return (
    <Box component="article" sx={{ bgcolor: '#F7F9FC' }}>
      <SEOHead
        title={t(`${p}.seoTitle`)}
        description={t(`${p}.seoDescription`)}
        canonical="/blog/tarjetas-newcomers-que-piden-bancos"
        routeKey="blogTarjetas"
        seoKey="blogTarjetas"
        noIndex={i18n.language !== 'es'}
        ogImage="https://www.finmaple.com/images/blog/tarjetas-credito-newcomers-canada.jpg"
        schema={articleSchema}
      />

      {/* Hero Image */}
      <Box
        role="img"
        aria-label={t(`${p}.heroAlt`)}
        sx={{
          width: '100%',
          height: { xs: 220, md: 360 },
          backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.45)), url(${HERO_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'flex-end',
        }}
      >
        <Container maxWidth="md" sx={{ pb: 4 }}>
          <Button
            component={RouterLink}
            to={`/${lng}/blog`}
            startIcon={<ArrowBack />}
            sx={{ color: '#fff', textTransform: 'none', mb: 2, fontWeight: 500 }}
          >
            {t(`${p}.backToGuides`)}
          </Button>
        </Container>
      </Box>

      <TrustStrip />

      {/* Article Content */}
      <Container maxWidth="md" sx={{ mt: 0, position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '14px',
            px: { xs: 3, md: 5 },
            py: { xs: 3, md: 4 },
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          }}
        >
          <BreadcrumbNav items={[{ label: t(`${p}.breadcrumbGuides`), to: `/${lng}/blog` }, { label: t(`${p}.breadcrumb`) }]} />
          {/* Tags */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {TAGS.map((tag, i) => (
              <Chip
                key={i}
                label={tag}
                size="small"
                sx={{
                  bgcolor: '#F0F2F5',
                  color: '#5F6B7A',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                }}
              />
            ))}
          </Box>

          {/* Title */}
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 600, color: '#1A1A1A', lineHeight: 1.2, mb: 3, fontSize: { xs: '1.75rem', md: '2.5rem' } }}
          >
            {t(`${p}.title`)}
          </Typography>

          {/* Meta */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 3,
              color: '#5F6B7A',
              mb: 3,
              fontSize: '0.9rem',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Person sx={{ fontSize: 18 }} />
              <a
                href={AUTHOR.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none', color: '#1E3A5F', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E3A5F' }}>
                  {AUTHOR.name}
                </Typography>
                <LinkedIn sx={{ fontSize: 16, color: '#1E3A5F' }} />
              </a>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarToday sx={{ fontSize: 16 }} />
              <Typography variant="body2">{DATE_TEXT}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTime sx={{ fontSize: 16 }} />
              <Typography variant="body2">{READ_TIME}</Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Atomic Answer */}
          <AtomicAnswer text={t(`${p}.atomicAnswer`)} />

          {/* Key Takeaways - Collapsible */}
          <Box
            sx={{
              border: '1px solid #EEF0F3',
              borderRadius: '10px',
              overflow: 'hidden',
              mt: 5,
              mb: 5,
            }}
          >
            <Box
              onClick={() => setTakeawaysOpen(!takeawaysOpen)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 1.5,
                bgcolor: '#F7F9FC',
                cursor: 'pointer',
                '&:hover': { bgcolor: '#EEEEEE' },
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                {t(`${p}.takeawaysTitle`)}
              </Typography>
              {takeawaysOpen ? <ExpandLess sx={{ color: '#5F6B7A' }} /> : <ExpandMore sx={{ color: '#5F6B7A' }} />}
            </Box>
            <Box component="ul" sx={{ display: takeawaysOpen ? 'block' : 'none', px: 3, py: 2, m: 0, '& li': { mb: 1, color: '#1A1A1A', lineHeight: 1.6 } }}>
                <li>{t(`${p}.takeaway1`)}</li>
                <li>{t(`${p}.takeaway2`)}</li>
                <li>{t(`${p}.takeaway3`)}</li>
                <li>{t(`${p}.takeaway4`)}</li>
                <li>{t(`${p}.takeaway5`)}</li>
              </Box>
          </Box>

          {/* H2: Lo que un banco realmente evalua */}
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t(`${p}.h2BankEval`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.bankEvalIntro`)}
          </Typography>
          <Box component="ol" sx={{ pl: 3, mb: 3, '& li': { mb: 1.5, color: '#1A1A1A', lineHeight: 1.7 } }}>
            <li>
              <strong>{t(`${p}.evalFactor1Label`)}</strong> {t(`${p}.evalFactor1Text`)}
            </li>
            <li>
              <strong>{t(`${p}.evalFactor2Label`)}</strong> {t(`${p}.evalFactor2Text`)}
            </li>
            <li>
              <strong>{t(`${p}.evalFactor3Label`)}</strong> {t(`${p}.evalFactor3Text`)}
            </li>
          </Box>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.bankEvalOutro1`)} <RouterLink to={`/${lng}/blog/construir-credito-canada-desde-cero`} style={{ color: '#1E3A5F', textDecoration: 'underline' }}>{t(`${p}.bankEvalOutroLink`)}</RouterLink>.
          </Typography>

          {/* FCAC Info Box */}
          <Box
            sx={{
              border: '1px solid #E6E9EE',
              borderRadius: '10px',
              p: 3,
              mb: 4,
              bgcolor: '#F7F9FC',
            }}
          >
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
              <strong>{t(`${p}.fcacLabel`)}</strong> {t(`${p}.fcacText`)}
            </Typography>
          </Box>

          {/* H2: Documentos tipicos */}
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t(`${p}.h2Documents`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 3 }}>
            {t(`${p}.documentsIntro`)}
          </Typography>

          {/* Checklist */}
          <Box sx={{ mb: 4 }}>
            {CHECKLIST.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  gap: 2,
                  mb: 2,
                  p: 2,
                  borderRadius: '10px',
                  bgcolor: '#fff',
                  border: '1px solid #E6E9EE',
                }}
              >
                <CheckCircleOutline sx={{ color: '#2E7D32', mt: 0.3, flexShrink: 0 }} />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.6 }}>
                    {item.detail}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* H2: SIN */}
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t(`${p}.h2SIN`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.sinIntro`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            <strong>{t(`${p}.sinGovLabel`)}</strong> {t(`${p}.sinGovText`)}
          </Typography>

          {/* Warning Box */}
          <Box
            sx={{
              border: '1px solid #FFE8A0',
              borderRadius: '10px',
              p: 3,
              mb: 3,
              bgcolor: '#FFFBF0',
              display: 'flex',
              gap: 2,
              alignItems: 'flex-start',
            }}
          >
            <WarningAmber sx={{ color: '#F9A825', mt: 0.3, flexShrink: 0 }} />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
                {t(`${p}.sinWarningTitle`)}
              </Typography>
              <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
                {t(`${p}.sinWarningText`)}
              </Typography>
            </Box>
          </Box>

          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            <strong>{t(`${p}.sinPracticeLabel`)}</strong> {t(`${p}.sinPracticeText`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 3 }}>
            <strong>{t(`${p}.sinTipLabel`)}</strong> {t(`${p}.sinTipText`)}
          </Typography>

          {/* H2: Newcomer Programs */}
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t(`${p}.h2Programs`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.programsIntro1`)} <RouterLink to={`/${lng}/blog/mejores-cuentas-sin-fees-recien-llegados`} style={{ color: '#1E3A5F', textDecoration: 'underline' }}>{t(`${p}.programsIntroLink`)}</RouterLink>{t(`${p}.programsIntro1End`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 3 }}>
            {t(`${p}.programsIntro2`)}
          </Typography>

          {/* Newcomer Programs Table */}
          <ArticleTable
            columns={[
              { key: 'banco', label: t(`${p}.colBank`) },
              { key: 'programa', label: t(`${p}.colProgram`) },
              { key: 'elegibilidad', label: t(`${p}.colEligibility`) },
              { key: 'ingresoVerificable', label: t(`${p}.colIncome`), align: 'center' },
              { key: 'limiteTipico', label: t(`${p}.colLimit`) },
            ]}
            rows={NEWCOMER_PROGRAMS.map((row) => ({
              ...row,
              limiteTipico: { text: row.limiteTipico, sx: { color: '#5F6B7A', fontWeight: 600 } },
            }))}
          />

          <Typography variant="body2" sx={{ color: '#5F6B7A', lineHeight: 1.6, mb: 4, fontStyle: 'italic' }}>
            {t(`${p}.tableDisclaimer`)}
          </Typography>

          {/* H2: Plan B - Secured Cards */}
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t(`${p}.h2SecuredCards`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.securedIntro1`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.securedIntro2`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 3 }}>
            {t(`${p}.securedIntro3`)}
          </Typography>

          {/* Secured Cards Options */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
            {SECURED_CARDS.map((card) => (
              <Box
                key={card.name}
                sx={{
                  flex: 1,
                  minWidth: 250,
                  border: '1px solid #E6E9EE',
                  borderRadius: '10px',
                  p: 3,
                  bgcolor: '#fff',
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
                  {card.name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#5F6B7A', lineHeight: 1.6, mb: 2 }}>
                  {card.desc}
                </Typography>
                <Box
                  sx={{
                    display: 'inline-block',
                    bgcolor: card.tagColor,
                    color: '#fff',
                    px: 2,
                    py: 0.75,
                    borderRadius: '14px',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                  }}
                >
                  {card.tag}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Graduation path */}
          <Box
            sx={{
              border: '1px solid #E6E9EE',
              borderRadius: '10px',
              p: 3,
              mb: 4,
              bgcolor: '#F7F9FC',
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 1 }}>
              {t(`${p}.graduationTitle`)}
            </Typography>
            <Box component="ol" sx={{ pl: 3, m: 0, '& li': { mb: 1, color: '#1A1A1A', lineHeight: 1.7 } }}>
              <li>{t(`${p}.gradStep1`)}</li>
              <li>{t(`${p}.gradStep2`)}</li>
              <li>{t(`${p}.gradStep3`)}</li>
              <li>{t(`${p}.gradStep4`)}</li>
              <li>{t(`${p}.gradStep5`)}</li>
            </Box>
          </Box>

          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            <strong>{t(`${p}.importantLabel`)}</strong> {t(`${p}.importantWaitText`)}
          </Typography>

          {/* H2: Multiple applications */}
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t(`${p}.h2MultipleApps`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t(`${p}.multipleAppsIntro1`)}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 3 }}>
            {t(`${p}.multipleAppsIntro2`)}
          </Typography>

          <Box component="ol" sx={{ pl: 3, mb: 3, '& li': { mb: 1.5, color: '#1A1A1A', lineHeight: 1.7 } }}>
            <li>
              <strong>{t(`${p}.strategy1Label`)}</strong> {t(`${p}.strategy1Text1`)} <RouterLink to={`/${lng}/tarjetas-de-credito`} style={{ color: '#1E3A5F', textDecoration: 'underline' }}>{t(`${p}.strategy1Link`)}</RouterLink>. {t(`${p}.strategy1Text2`)}
            </li>
            <li>
              <strong>{t(`${p}.strategy2Label`)}</strong> {t(`${p}.strategy2Text`)}
            </li>
            <li>
              <strong>{t(`${p}.strategy3Label`)}</strong> {t(`${p}.strategy3Text`)}
            </li>
            <li>
              <strong>{t(`${p}.strategy4Label`)}</strong> {t(`${p}.strategy4Text`)}
            </li>
          </Box>

          {/* Pre-qualification info */}
          <Box
            sx={{
              border: '1px solid #E6E9EE',
              borderRadius: '10px',
              p: 3,
              mb: 4,
              bgcolor: '#F7F9FC',
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 1 }}>
              {t(`${p}.prequalTitle`)}
            </Typography>
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
              {t(`${p}.prequalText`)}
            </Typography>
          </Box>

          <Divider sx={{ mt: 5, mb: 0 }} />

          {/* FAQ Section */}
          <FAQSection faqs={FAQS} />


          {/* Interlink CTA Box */}
          <Box
            sx={{
              bgcolor: '#2E7D32',
              borderRadius: '10px',
              p: 3,
              mt: 4,
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600, mb: 0.5 }}>
                {t(`${p}.ctaCompareTitle`)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
                {t(`${p}.ctaCompareDesc`)}
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to={`/${lng}/tarjetas-de-credito`}
              variant="contained"
              endIcon={<ArrowForward />}
              sx={{
                bgcolor: '#fff',
                color: '#2E7D32',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '14px',
                px: 3,
                flexShrink: 0,
                '&:hover': { bgcolor: '#E8F5E9' },
              }}
            >
              {t(`${p}.ctaCompareBtn`)}
            </Button>
          </Box>

          {/* Second CTA - Bank Comparison */}
          <Box
            sx={{
              bgcolor: '#1E3A5F',
              borderRadius: '10px',
              p: 3,
              mt: 2,
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600, mb: 0.5 }}>
                {t(`${p}.ctaBankTitle`)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
                {t(`${p}.ctaBankDesc`)}
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to={`/${lng}/comparar/mejores-bancos-recien-llegados-canada-2026`}
              variant="contained"
              endIcon={<ArrowForward />}
              sx={{
                bgcolor: '#fff',
                color: '#1E3A5F',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '14px',
                px: 3,
                flexShrink: 0,
                '&:hover': { bgcolor: '#E8EAF6' },
              }}
            >
              {t(`${p}.ctaBankBtn`)}
            </Button>
          </Box>

          {/* Official Sources */}
          <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid #E6E9EE' }}>
            <Typography variant="subtitle2" sx={{ color: '#5F6B7A', fontWeight: 600, mb: 1.5 }}>
              {t(`${p}.sourcesTitle`)}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Link href="https://www.canada.ca/en/financial-consumer-agency/services/credit-cards.html" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t(`${p}.source1`)} <OpenInNew sx={{ fontSize: 14 }} />
              </Link>
              <Link href="https://www.canada.ca/en/employment-social-development/services/sin.html" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t(`${p}.source2`)} <OpenInNew sx={{ fontSize: 14 }} />
              </Link>
              <Link href="https://www.canada.ca/en/financial-consumer-agency/services/credit-reports-score.html" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t(`${p}.source3`)} <OpenInNew sx={{ fontSize: 14 }} />
              </Link>
            </Box>
            <Typography variant="caption" sx={{ color: '#5F6B7A', display: 'block', mt: 2 }}>
              {t(`${p}.updatedNote`)}
            </Typography>
          </Box>

          {/* Author Bio */}
          <Divider sx={{ mt: 5, mb: 0 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, p: 3, bgcolor: '#F7F9FC', borderRadius: '12px', mt: 3 }}>
            <Box component="img" src={AUTHOR.photo} alt={AUTHOR.name} loading="lazy" sx={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3 }}>{AUTHOR.name}</Typography>
              <Typography variant="body2" sx={{ color: '#5F6B7A' }}>{t(`${p}.authorRole`)}</Typography>
            </Box>
            <Button href={AUTHOR.linkedin} target="_blank" rel="noopener noreferrer" startIcon={<LinkedIn />} size="small" sx={{ textTransform: 'none', color: '#1E3A5F', fontWeight: 600, borderColor: '#1E3A5F', '&:hover': { bgcolor: 'rgba(10,102,194,0.08)' } }} variant="outlined">LinkedIn</Button>
          </Box>
        </Box>

        <RelatedArticles slug="tarjetas-newcomers-que-piden-bancos" />
      </Container>

      <NewsletterSection />
    </Box>
  );
};

export default TarjetasNewcomersPage;
